import {
  Backdrop,
  CircularProgress,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import React, { useRef, useState } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import { useDebounceEffect } from "../../helpers";
import { canvasPreview } from "./CanvasPreview";
import { myExpariencesService } from "../../services";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import BasketCourt from "../../assets/basketCourt.png";
import PickleballCourt from "../../assets/pickleballCourt.png";

const PREFIX = "imageCropper";

const classes = {
  imagePreview: `${PREFIX}-imagePreview`,
  reactCropStyle: `${PREFIX}-reactCropStyle`,
};

const ImageCropperContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.imagePreview}`]: {
      width: "50px",
    },
    [`& .${classes.reactCropStyle}`]: {
      "& > div:last-child > div:last-child": {
        pointerEvents: "none",
      },
    },
  };
});

const ImageCropper = ({
  imgUrl,
  aspect,
  urlSetter,
  urlToCrop,
  isCourt,
  isPickleball = false,
}) => {
  const { id } = useParams();
  const theme = useTheme();
  const { t } = useTranslation();
  const enqueueSnackbar = useSnackbar();
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [isResizing, setIsResizing] = useState(false);
  const previewCanvasRef = useRef();
  const imgRef = useRef();
  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role === "ADMIN"
  );

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
      }
    },
    100,
    [completedCrop]
  );

  const centerAspectCrop = (mediaWidth, mediaHeight, aspect) => {
    return centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 100,
        },
        aspect,
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    );
  };

  const onImageLoad = (e) => {
    if (aspect) {
      const { width, height } = e.currentTarget;
      if (
        imgRef.current.naturalWidth > 4096 ||
        imgRef.current.naturalHeight > 4096
      ) {
        setIsResizing(true);
        const urlSplitted = imgUrl.split("/");
        myExpariencesService
          .getImageUrlFromS3(
            isAdmin,
            id,
            imgUrl,
            true,
            urlSplitted[urlSplitted.length - 1]
          )
          .then((res) => {
            if (urlToCrop) urlSetter({ ...urlToCrop, url: res.url });
            else urlSetter(res.url);
            setIsResizing(false);
          })
          .catch((err) => {
            if (err === 403)
              enqueueSnackbar(t("toasts.unauthorizedError"), {
                variant: "error",
              });
            else
              enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
            setIsResizing(false);
          });
      } else {
        setCrop(centerAspectCrop(width, height, aspect));
      }
    }
  };

  return (
    <ImageCropperContainer>
      <Backdrop
        open={isResizing}
        sx={{
          zIndex: theme?.zIndex.drawer + 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CircularProgress size={50} />
        <Typography variant="body1" sx={{ marginTop: "1rem" }}>
          {t("loaders.resizing")}
        </Typography>
      </Backdrop>
      <ReactCrop
        crop={crop}
        onChange={(_, percentCrop) => setCrop(percentCrop)}
        onComplete={(c) => setCompletedCrop(c)}
        aspect={aspect}
        minHeight={100}
        className={classes.reactCropStyle}
        renderSelectionAddon={() =>
          isCourt && !isPickleball ? (
            <img
              src={BasketCourt}
              alt="basket court"
              style={{ width: "100%" }}
            />
          ) : isCourt && isPickleball ? (
            <img
              src={PickleballCourt}
              alt="pickleball court"
              style={{ width: "100%" }}
            />
          ) : null
        }
      >
        <img
          crossOrigin="anonymous"
          ref={imgRef}
          src={imgUrl}
          alt="cropping selection"
          classes={classes.imagePreview}
          onLoad={onImageLoad}
        />
      </ReactCrop>
      <canvas
        ref={previewCanvasRef}
        id="cropping-canvas"
        style={{
          display: "none",
        }}
      />
    </ImageCropperContainer>
  );
};

export default ImageCropper;
